import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateDeviceInstance } from '../../api/deviceInstances';
import type { DeviceInstance } from '../../types';

interface DeviceInstanceEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedInstance: DeviceInstance | null;
  projectId: number;
  onSuccess?: () => void; // Callback to trigger data reload
}

export default function DeviceInstanceEditModal({ 
  isOpen, 
  onClose, 
  selectedInstance, 
  projectId,
  onSuccess 
}: DeviceInstanceEditModalProps) {
  const [mouseDownTarget, setMouseDownTarget] = useState<EventTarget | null>(null);
  
  const queryClient = useQueryClient();

  // Close modal on ESC key
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  // Update instance mutation
  const updateInstanceMutation = useMutation({
    mutationFn: async (data: { id: number; updates: Partial<DeviceInstance> }) => {
      await updateDeviceInstance(data.id, data.updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deviceInstances', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projectDevices', projectId] });
      onClose();
      onSuccess?.(); // Call the onSuccess prop
    },
    onError: (error) => {
      console.error('Error updating device instance:', error);
    }
  });

  const handleEditInstance = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedInstance) return;
    
    const formData = new FormData(e.currentTarget);
    
    const updates = {
      position_label: (formData.get('position_label') as string || '').trim() || null,
      description: (formData.get('description') as string || '').trim() || null,
      knx_address: (formData.get('knx_address') as string || '').trim() || null,
      is_reserve: formData.get('is_reserve') ? 1 : 0
    };

    updateInstanceMutation.mutate({ id: selectedInstance.id, updates });
  };

  if (!isOpen || !selectedInstance) return null;

  // Debug: log selectedInstance data to check device_type_id
  console.log('🔍 DeviceInstanceEditModal - ID:', selectedInstance.id);
  console.log('🔍 device_type_id:', selectedInstance.device_type_id);
  console.log('🔍 spec_name:', selectedInstance.spec_name);
  console.log('🔍 knx_address:', selectedInstance.knx_address);
  console.log('🔍 manufacturer:', selectedInstance.manufacturer);
  console.log('🔍 model:', selectedInstance.model);
  console.log('🔍 ALL DATA:', JSON.stringify(selectedInstance, null, 2));

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onMouseDown={(e) => setMouseDownTarget(e.target)}
      onMouseUp={(e) => {
        if (e.target === mouseDownTarget && e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold mb-4">
          Редактировать экземпляр устройства <span className="instance-id">#{selectedInstance.id}</span>
        </h3>
        
        {/* Parent specification info - compact */}
        <div className="mb-3 px-3 py-2 bg-gray-50 border border-gray-200 rounded text-xs text-gray-600">
          <span className="font-medium">Спецификация:</span> ID {selectedInstance.specification_id} • {selectedInstance.spec_name || '—'}
          {selectedInstance.manufacturer && <span> • {selectedInstance.manufacturer}</span>}
          {selectedInstance.model && <span> • {selectedInstance.model}</span>}
        </div>
        
        <form onSubmit={handleEditInstance} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Позиционное обозначение</label>
            <input
              type="text"
              name="position_label"
              defaultValue={selectedInstance.position_label || ''}
              placeholder="напр., ДП-14.2-1, LD-1-1, УР-2-4"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Описание</label>
            <textarea
              name="description"
              defaultValue={selectedInstance.description || ''}
              placeholder="Дополнительное описание экземпляра..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>

          {/* KNX Address field - show for KNX devices (device_type_id = 14) and Actuators (device_type_id = 5) */}
          {(selectedInstance.device_type_id === 14 || selectedInstance.device_type_id === 5) && (
            <div>
              <label className="block text-sm font-medium mb-1">KNX адрес</label>
              <input
                type="text"
                name="knx_address"
                defaultValue={selectedInstance.knx_address || ''}
                placeholder="напр., 1.1.1"
                pattern="^\d+\.\d+\.\d+$"
                title="Формат: 1.255.255"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
              <p className="text-xs text-gray-500 mt-1">Формат: 1.255.255</p>
            </div>
          )}

          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_reserve_device"
              name="is_reserve"
              defaultChecked={Boolean(selectedInstance?.is_reserve)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="is_reserve_device" className="ml-2 text-sm font-medium text-gray-700">
              Резервный
            </label>
          </div>

          <div className="flex space-x-2 pt-4">
            <button
              type="submit"
              disabled={updateInstanceMutation.isPending}
              className="flex-1 bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {updateInstanceMutation.isPending ? 'Сохранение...' : 'Сохранить'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-600 text-white py-2 rounded-md hover:bg-gray-700"
            >
              Отмена
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 